import dynamic from 'next/dynamic'
const Dialog = dynamic(
	() => import('@qr-front/ui/components/Dialog').then((mod) => mod.Dialog),
	{ ssr: false },
)
const DialogContent = dynamic(
	() =>
		import('@qr-front/ui/components/Dialog').then((mod) => mod.DialogContent),
	{ ssr: false },
)

import { LineAlertTriangle } from '@qr-front/icons/line-alert-triangle'
import { LineDownload } from '@qr-front/icons/line-download'
import { Button } from '@qr-front/ui/components/Button'
import { buildQrLandingUrl } from 'features/generator/utils/buildQrLandingUrl'
import {
	downloadQrDialogAtom,
	downloadQrTypeAtom,
	qrCodeAtom,
} from 'features/library/client'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { useTranslations } from 'next-intl'

import { QR_TYPE, qrCodesApi } from '@/generator/shared'
import { Link } from '@/i18n-routing'

import { scanQrDialogAtom, scanQrDialogDataAtom } from '../dialogsState'

export const ScanQrDialog = () => {
	const [open, setOpen] = useAtom(scanQrDialogAtom)

	const qrItemData = useAtomValue(scanQrDialogDataAtom)
	const setQrCode = useSetAtom(qrCodeAtom)
	const setOpenQrDownloadDialog = useSetAtom(downloadQrDialogAtom)
	const setDownloadQrType = useSetAtom(downloadQrTypeAtom)

	const t = useTranslations()

	if (!open || !qrItemData?.previewSvgString || !qrItemData?.uri) return null

	const { previewSvgString, uri, disabled, id, content } = qrItemData

	const isWifiQrType = content?.type === QR_TYPE.Wifi
	const qrLandingUrl = uri ? buildQrLandingUrl(uri) : ''
	const qrLandingUrlWithoutProtocol = qrLandingUrl.replace(/^https?:\/\//, '')

	const handleDownloadClick = async () => {
		try {
			const response = await qrCodesApi.getQr(id)
			setQrCode(response.data)
		} catch (err) {
			setOpen(false)
			console.log('Error downloading qr code', err)
		} finally {
			setOpen(false)
			setDownloadQrType('downloadAsPng')
			setOpenQrDownloadDialog(true)
		}
	}

	return (
		<Dialog open={open} onOpenChange={() => setOpen(!open)}>
			<DialogContent closeButtonLabel={t('ui__popup__close_button')}>
				<div className="flex flex-col items-center justify-center gap-6 desktop:gap-10">
					<div className="flex flex-col gap-2 text-center tablet:px-6">
						<h3 className="type-heading-3">
							{t('dashboard__scan__qr_code__modal_title')}
						</h3>
						<p className="type-body-medium text-dark-grey">
							{t('dashboard__scan__qr_code__modal_description')}
						</p>
					</div>
					<div className="flex flex-col items-center justify-center gap-4">
						<div
							dangerouslySetInnerHTML={{ __html: previewSvgString }}
							className="size-40 [&>svg]:size-40"
						/>
						{qrLandingUrl && !isWifiQrType && (
							<Link
								href={qrLandingUrl}
								target="_blank"
								prefetch={false}
								className="type-body-small text-blue"
							>
								{qrLandingUrlWithoutProtocol}
							</Link>
						)}
					</div>
					{!disabled ? (
						<Button
							visual="default"
							className="type-body-small min-w-[210px]"
							onClick={handleDownloadClick}
						>
							<LineDownload className="size-4" />
							{t('dashboard__qr_table__qr_card__download')}
						</Button>
					) : (
						<div className="flex flex-col items-center justify-center gap-1">
							<LineAlertTriangle className="text-error" />
							<p className="type-body-medium text-error">
								{t('dashboard__share__qr_code__modal__paused_qr_warning')}
							</p>
						</div>
					)}
				</div>
			</DialogContent>
		</Dialog>
	)
}
